import { flow, types, getEnv, Instance } from "mobx-state-tree";

import { DataGridStorageModel } from "../../components/datatable/DataGridStorage";
import { Column, ColumnType } from "../../components/datatable/interfaces";
import { arrayItemMove, arrayItemMoveFirst } from "../../helpers/Collections";
import { endpoints } from "../api/ApiEndpoints";
import { Timestamps, Timestamp } from "../video-player/TimestampsData";
import { VideoPlayerDamage } from "./VideoPlayerDamage";
import serializeToQueryParams from "../../helpers/QueryParams";
import { Damage } from "./Damage";
import { STATION_FIELD, VIDEOPOSITION_FIELD } from "../../api-models/response/BluemetricFields";
import {
  DataGridResponseWithDynamicProperties,
  HeaderItemResponse,
  RowResponse,
} from "../../api-models/response/DataGridResponse";

const DamageNumberKey = "DamageNumber";
const ModifiedKey = "Modified";
const StationKey = "BM101";

const esicTableColumns: Column[] = [
  {
    key: DamageNumberKey,
    name: "general.damages.list-header.damageNumber",
    sortable: true,
    containsLink: true,
  },
  {
    key: ModifiedKey,
    name: "general.last-edit",
    type: ColumnType.DateTime,
    sortable: true,
    filterable: true,
  },
];

const DamagesFilter = types.model({
  inspectionId: types.number,
  hasIpfFile: types.optional(types.boolean, false),
  preview: types.optional(types.boolean, false),
  videoPlayerTimestamps: types.optional(types.frozen(), new Timestamps([], "")),
  sortByStation: types.optional(types.boolean, false)
});

export const DamagesStorageModel = types
  .compose(DamagesFilter, DataGridStorageModel)
  .named("DamagesStorageModel")
  .actions((self) => {
    const moveModifiedItemToLastHeaderItem = (headerItems: Column[]) => {
      const modifiedIndex = headerItems.findIndex((x) => x.key === ModifiedKey);
      if (modifiedIndex !== -1) arrayItemMove(headerItems, modifiedIndex, headerItems.length);
    };

    const moveStationToFirstHeaderItem = (headerItems: Column[]) => {
      const damageNoIndex = headerItems.findIndex((x) => x.key === StationKey);
      if (damageNoIndex !== -1) arrayItemMoveFirst(headerItems, damageNoIndex);
    };

    const getHeaders = (response: DataGridResponseWithDynamicProperties) => {
      const tableHeaders = response.header as HeaderItemResponse[];
      if (!tableHeaders.length) return esicTableColumns;

      const BMColumns = tableHeaders
        .filter((header) => esicTableColumns.every((x) => x.key !== header.key))
        .map((column: HeaderItemResponse) => {
          return {
            key: column.key,
            name: column.key === VIDEOPOSITION_FIELD ? "general.time" : column.name,
            type: self.getColumnType(column.metadata),
            valueOptions: self.getColumnPredefinedValueOptions(column.metadata),
            sortable: true,
            filterable: column.key !== VIDEOPOSITION_FIELD,
            containsLink: column.key === VIDEOPOSITION_FIELD,
            flex: column.key === VIDEOPOSITION_FIELD ? 1.3 : undefined
          };
        });

      const allColumns = esicTableColumns.concat(BMColumns);

      if (allColumns.length) {
        moveStationToFirstHeaderItem(allColumns);
        moveModifiedItemToLastHeaderItem(allColumns);
      }
      return allColumns;
    };

    return {
      afterCreate() {
        self.defaultSort = {
          field: self.hasIpfFile ? STATION_FIELD : VIDEOPOSITION_FIELD,
          sort: "asc",
        };
        if (self.sortByStation) self.defaultSort = {field: STATION_FIELD, sort: "asc"}
      },
      handleCollapse(isCollapsed: boolean) {
        self.isCollapsed = !isCollapsed;

        self.defaultColumnVisibility = self.gridColumns?.reduce((columnVisibility, column) => {
          columnVisibility[column.key] =
            column.key === DamageNumberKey
              ? true
              : column.key === ModifiedKey
                ? false
                : isCollapsed;
          return columnVisibility;
        }, {});
      },
      mapItem(item: RowResponse) {
        return new Damage(item);
      },
      mapResponse(response: DataGridResponseWithDynamicProperties) {
        self.data = {
          totalCount: response.totalCount,
          items: response.rows.map(self.mapItem),
        };
        self.gridColumns = getHeaders(response);
      },
      deleteDamages: flow(function* deleteDamages(ids: number[]) {
        const endpoint = endpoints.get("damages");
        yield getEnv(self).api.delete(endpoint, ids);
        self.deselectAll();
        yield self.load();
      }),
      getDamagesForPlayer: flow(function* getDamagesForPlayer(inspectionId: number) {
        const endpoint = endpoints.get("damages");
        endpoint.queryParams = `inspectionId=${inspectionId}`;

        const { rows = [] }: DataGridResponseWithDynamicProperties = yield getEnv(self).api.get(
          endpoint
        );

        const damages: VideoPlayerDamage[] = rows.map((damageRow) => {
          const damage = damageRow.details;
          return new VideoPlayerDamage({
            displayValues: {
              ...damage,
            },
            id: damageRow.id,
          });
        });
        const damagesSortedByPosition = damages.sort((a, b) => a.videoPosition - b.videoPosition);

        self.videoPlayerTimestamps = new Timestamps(
          damagesSortedByPosition.map(
            (x) =>
              new Timestamp({
                timestamp: x.videoPosition,
                id: x.id,
                text: x.description,
                mainCode: x.mainCode,
                c1Code: x.c1Code,
                c2Code: x.c2Code,
              })
          ),
          "damages/:id"
        );
      }),
    };
  })
  .views((self) => ({
    get canLoadItems() {
      return !!self.inspectionId;
    },
    get endpoint() {
      const endpoint = self.useGridFiltering
        ? endpoints.get("damages_all")
        : endpoints.get("damages");
      if (!endpoint) return endpoint;

      const sortedColumn = self.getSortQuery();

      endpoint.queryParams = self.preview
        ? sortedColumn
          ? serializeToQueryParams({
            inspectionId: self.inspectionId,
            preview: true,
            ...sortedColumn,
          })
          : serializeToQueryParams({ inspectionId: self.inspectionId, preview: true })
        : `inspectionId=${self.inspectionId}&${self.getDataQuery()}`;

      return endpoint;
    },
    get damageDetailsForDiagram() {
      return (self.data.items as Damage[]).map((damage) => damage.diagramDetails);
    },
  }));

export type DamagesStorage = Instance<typeof DamagesStorageModel>;
